import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get, map, intersection } from 'lodash';
import classNames from 'classnames';
import { Element } from 'react-scroll';
import { Button } from '@ookla/common-ui';
import Datepicker from './datepicker/datepicker';
import { AwardSection, AwardsComponent } from './award/award-section';
import Award from './award/award';
import PageNav from './page-nav/page-nav';
import c from './../utils/constants';
import * as utilFormat from './../utils/format';
import * as utilAwards from './../utils/awards';
import awardStyles from './award/award.module.css';
import awardSectionStyles from './award/award-section.module.css';
import datepickerStyles from './datepicker/datepicker.css';

import isp2017 from './../images/badges/Fixed2017@2x.png';
import carrier2017 from './../images/badges/Mobile2017@2x.png';
import best2017 from './../images/badges/Best2017@2x.png';
import badge2018 from './../images/badges/Badge2018@2x.png';
import badge2019 from './../images/badges/Badge2019.png';
import badge2020 from './../images/badges/Badge2020.svg';
import badge2021 from './../images/badges/Badge2021.svg';
import badge2022 from './../images/badges/Badge2022.svg';
import badge2023 from './../images/badges/Badge2023.svg';
import badge2024 from './../images/badges/Badge2024.svg';

const badgeImages = {
  isp2017,
  carrier2017,
  best2017,
  badge2018,
  badge2019,
  badge2020,
  badge2021,
  badge2022,
  badge2023,
  badge2024
};

const AwardsList = (props) => {

  const { awards, getAwardProps } = props;

  return awards?.length
    ? awards.map((award) => {
      return (
        <Element name={award.id} key={award.id}>
          <Award key={award.winner} {...getAwardProps(award)} />
        </Element>
      );
    })
    : 'No awards available for the selected criteria';
};

export default class Container extends Component {
  static propTypes = {
    timePeriod: PropTypes.string,
    locationSlug: PropTypes.string,
    locationName: PropTypes.string,
    template: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
    // indicates the criteria by which the awards are grouped in sections
    groupedBy: PropTypes.string,
    // if truthy should render the single award layout
    singleAward: PropTypes.object,
    methodologyLink: PropTypes.string.isRequired,
    sections: PropTypes.object,
    navItems: PropTypes.object,
    datepickerData: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.number,
        edges: PropTypes.array
      })
    ),
    handleDatepickerSelect: PropTypes.func.isRequired,
    datepickerSelectedOption: PropTypes.string.isRequired
  };

  getAwardProps = (award = {}) => {
    const { template } = this.props;
    const normalizeRankings = (rankings, awardType) => {
      if (!rankings) {
        return null;
      }
      return rankings.map((ranking) => {
        return {
          ...ranking,
          companyName: get(ranking, `${awardType}_name`)
        };
      });
    };

    const header = template === 'type' ? award.winner : c.awardTypes[award.type].label;
    const subHeader = template === 'location' ? award.winner : award.locationName;

    return {
      header,
      subHeader,
      awardColor: c.awardTypes[award.type].color,
      report: award.report,
      report2: award.report2,
      report3: award.report3,
      companyBlurb: award.providerCopy,
      tooltips: get(award, 'data.tooltips'),
      awardPageRoute:
        template === 'location' && this.props.awardType
          ? null
          : utilFormat.getSigleAwardRoute(award.locationName, award.year, award.type, award.timePeriod),
      rankings: normalizeRankings(get(award, 'data.rankings'), award.type)
    };
  };




  renderTimePeriodSections = (sections, groupedBy, template) => {
    const timePeriodOrder = intersection(c.timePeriods, Object.keys(sections));

    if (groupedBy === 'timePeriodAndType') {
      return timePeriodOrder.map((timePeriod) => {
        let timePeriodAwards = [];
        const awardTypeSectionOrder = utilAwards.computeAwardTypeSectionOrder(template, sections[timePeriod]);

        awardTypeSectionOrder.forEach((type) => {
          // Merge awards from each type into a single array
          timePeriodAwards = [...timePeriodAwards, ...sections[timePeriod][type]];
        });

        return timePeriodAwards.length ? (
          <AwardSection key={timePeriod} header={timePeriod} awards={timePeriodAwards}>
            <AwardsList awards={timePeriodAwards} getAwardProps={this.getAwardProps} />
          </AwardSection>
        ) : null;
      });
    } else if (groupedBy === 'timePeriod' || groupedBy === 'type') {
      const sectionOrder =
        groupedBy === 'timePeriod' ? timePeriodOrder : utilAwards.computeAwardTypeSectionOrder(template, sections);
      const world = {};
      const regional = {};
      const country = {};
      sectionOrder.forEach((key) => {

        const awards = sections[key];
        awards.forEach((award) => {
          if (award.locationName === 'World') {
            if (!world[key]) {
              world[key] = [];
            }
            world[key].push(award);
            return;
          }
          if (c.navigationGroups.regional && c.navigationGroups.regional[award.locationName]) {
            if (!regional[key]) {
              regional[key] = [];
            }
            regional[key].push(award);
            return;
          }

          if (!country[key]) {
            country[key] = [];
          }
          country[key].push(award);
        });
      });

      const worldAwards = sectionOrder.map((key) => {
        if (world[key]) {
          let header = '';
          // Break awards into world, region, country
          if (groupedBy === 'type') {
            header = c.awardTypes[key].label;
          } else if (groupedBy === 'timePeriod') {
            header = key;
          }
          return (
            <AwardSection key={key} header={header} awards={world[key]}>
              <AwardsList awards={world[key]}  getAwardProps={this.getAwardProps} />
            </AwardSection>
          );
        }
      }).filter(function( element ) {
        return element !== undefined;
      });

      const regionalAwards = sectionOrder.map((key) => {
        if (regional[key] && regional[key].length > 0) {
          let header = '';
          // Break awards into world, region, country

          if (groupedBy === 'type') {
            header = c.awardTypes[key].label;
          } else if (groupedBy === 'timePeriod') {
            header = key;
          }
          return (
            <AwardSection key={key} header={header} awards={regional[key]}>
              <AwardsList awards={regional[key]}  getAwardProps={this.getAwardProps} />
            </AwardSection>
          );
        }
      }).filter(function( element ) {
        return element !== undefined;
      });

      const countryAwards = sectionOrder.map((key) => {
        if (country[key] && country[key].length > 0) {
          let header = '';
          // Break awards into world, region, country

          if (groupedBy === 'type') {
            header = c.awardTypes[key].label;
          } else if (groupedBy === 'timePeriod') {
            header = key;
          }
          return (
            <AwardSection key={key} header={header} awards={country[key]}>
              <AwardsList awards={country[key]}  getAwardProps={this.getAwardProps} />
            </AwardSection>
          );
        }
      }).filter(function( element ) {
        return element !== undefined;
      });

      return (
        <div className={awardSectionStyles.awardsSection}>
          <AwardsComponent awards={worldAwards} header={'World Awards'} />
          <AwardsComponent awards={regionalAwards} header={'Regional Awards'} />
          <AwardsComponent awards={countryAwards} header={'Country Awards'} />
        </div>

      );



    }

    return null;
  };

  renderSingleAward = (award) => {
    return <Award {...this.getAwardProps(award)} className={awardStyles.single} expandable={false} />;
  };

  // Returns an array of Objects representing all options in a datepicker dropdown.
  //
  // @param datepickerData {object} {
  //   year {number},
  //   edges
  // }
  renderDatepickerOptions = (datepickerData) => {
    // datepickerData should be a flat array
    const options = utilFormat.edgesToDatepickerOptions(datepickerData);

    return map(options, (option) => {
      return {
        value: utilFormat.getDatepickerOption(option.year, option.timePeriod),
        label: option.timePeriod ? option.timePeriod.toUpperCase() : String(option.year),
        className: option.timePeriod
          ? classNames(datepickerStyles.option, datepickerStyles.subOption)
          : classNames(datepickerStyles.option)
      };
    });
  };

  // Returns an ordered array of Objects representing all options in a datepicker dropdown.
  //
  // @param datepickerData {object}
  // {
  //   value {string} string representation year/period uid. If no period, value is the year. Examples: '2018', '2018/q1-q4',
  //   label {string} the label to display in datepicker. Examples: '2018', 'Q1-Q4'
  //   className
  // }
  orderDatepickerOptions = (datepickerData) => {
    return datepickerData.sort((a, b) => {
      // Order in two dimensions: first by year, then by predefined time period order.
      //
      // Note that year timePeriods aren't in the timePeriods constant array and will
      // cause the indexOf() to return -1. This is acceptable b/c they should
      // appear before the actual time periods on the list
      const aIndex = c.timePeriods.indexOf(a.label.toLowerCase());
      const bIndex = c.timePeriods.indexOf(b.label.toLowerCase());

      const aYear = Number(a.value.split('/')[0]);
      const bYear = Number(b.value.split('/')[0]);

      if (aYear === bYear) {
        return aIndex < bIndex ? -1 : 1;
      }
      return bYear - aYear;
    });
  };


  renderBadge = (awardType, year) => {
    if (String(year) === '2018' || String(year) === '2019') {
      return <img src={badgeImages[`badge${year}`]} className={awardStyles.badge} alt="" />;
    }
    if (String(year) === '2020' || String(year) === '2021' || String(year) === '2022' || String(year) === '2023' || String(year) === '2024') {
      return (
        <img src={`data:image/svg+xml;utf8,${badgeImages[`badge${year}`]}`} className={awardStyles.badge} alt="" />
      );
    }
    return <img src={badgeImages[`${awardType}${year}`]} className={awardStyles.badge} alt="" />;
  };

  render() {
    const {
      awardType,
      template,
      year,
      sections,
      singleAward,
      groupedBy,
      navItems,
      datepickerData,
      datepickerSelectedOption,
      handleDatepickerSelect,
      locationName,
      methodologyLink
    } = this.props;

    return (
      <div>
        <div className="pure-g">
          <div className="pure-u-1 pure-u-md-1-4 u-c">
            <div className={awardStyles.badgeWrapper}>
              {/* wrap the badge in a div so the flexbox styles don't stretch it */}
              <div>{singleAward ? this.renderBadge(awardType, year) : null}</div>
            </div>
            {singleAward ? null : (
              <Datepicker
                selectedOption={datepickerSelectedOption}
                onDateSelected={handleDatepickerSelect}
                options={this.orderDatepickerOptions(this.renderDatepickerOptions(datepickerData))}
              />
            )}
            {singleAward ? null : <PageNav items={navItems} />}
          </div>
          <div
            className={`${awardStyles.awardContainer} ${
              singleAward ? awardStyles.singleAwardContainer : null
            } pure-u-1 pure-u-md-3-4 u-c`}
          >
            {singleAward
              ? this.renderSingleAward(singleAward)
              : this.renderTimePeriodSections(sections, groupedBy, template)}
          </div>
        </div>
        <div className="pure-g">
          <div className="pure-u-1 pure-u-md-1-4 u-c" />
          <div className="pure-u-1 pure-u-md-3-4 u-c">
            <div className={awardStyles.methodology}>
              {// add callout on India pages to view previous awards
                template === 'location' && locationName === 'India' ? (
                  <p className={awardStyles.awardReport}>
                    <a href="https://www.speedtest.net/legacy-awards/in/carrier/2016" target="_blank">
                      To view previous awards click here
                    </a>
                  </p>
                ) : null}
            </div>
            <div className={awardStyles.methodology}>
              <a href={methodologyLink}>
                <Button theme="outline" icon="methodology">
                  Our Methodology
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
